'use client';

import React, { useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { XCircleIcon } from '@heroicons/react/20/solid';
import { PrimaryButton, SecondaryButton } from '@/components/platform/buttons';
import EmailInput from '@/components/platform/forms/EmailInput';
import PermissionsTreeSelector, {
  RoleType,
} from '@/components/platform/permissions/PermissionsTreeSelector';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (email: string, roles: RoleType[]) => Promise<void>;
  isAdmin?: boolean;
  userRoles?: RoleType[]; // Current user's roles (for permission validation)
}

export default function InviteUserModal({
  isOpen,
  onClose,
  onInvite,
  isAdmin = false,
  userRoles = [],
}: InviteUserModalProps) {
  const { darkMode } = useDarkMode();
  const [email, setEmail] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<RoleType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Handle roles change from PermissionsTreeSelector
  const handleRolesChange = (newRoles: RoleType[]) => {
    setSelectedRoles(newRoles);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Please enter an email address');
      return;
    }

    if (selectedRoles.length === 0) {
      setError('Please select at least one role');
      return;
    }

    setLoading(true);
    try {
      await onInvite(email, selectedRoles);
      setEmail('');
      setSelectedRoles([]);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/75 backdrop-blur-sm transition-opacity duration-300 ease-out data-closed:opacity-0"
      />

      <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            className={`relative w-full max-w-lg transform overflow-hidden rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transition-all duration-300 ease-out data-closed:scale-95 data-closed:opacity-0 sm:p-6 ${
              darkMode ? 'border border-white/10 bg-gray-800' : 'bg-white'
            }`}
          >
            {/* Close Button */}
            <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
              <button
                type="button"
                onClick={onClose}
                className={`rounded-md focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden ${
                  darkMode
                    ? 'bg-gray-800 text-gray-400 hover:text-gray-300'
                    : 'bg-white text-gray-400 hover:text-gray-500'
                }`}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>

            {/* Header */}
            <div className="mb-6">
              <DialogTitle
                className={`text-base font-semibold ${
                  darkMode ? 'text-gray-100' : 'text-gray-900'
                }`}
              >
                Invite User
              </DialogTitle>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Email Input */}
              <div className="mb-6">
                <EmailInput
                  label="Email Address"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                  validateOnBlur={true}
                  validateOnChange={false}
                />
              </div>

              {/* Role Selection */}
              <div className="mb-6">
                <label
                  className={`mb-3 block text-xs font-semibold ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  Permissions
                </label>

                <PermissionsTreeSelector
                  selectedRoles={selectedRoles}
                  onRolesChange={handleRolesChange}
                  isAdmin={isAdmin}
                  canEditAdminRole={true}
                  mode="invite"
                  userRoles={userRoles}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className={`mb-4 rounded-md p-4 ${darkMode ? 'bg-red-500/10' : 'bg-red-50'}`}>
                  <div className="flex">
                    <div className="shrink-0">
                      <XCircleIcon
                        aria-hidden="true"
                        className={`size-5 ${darkMode ? 'text-red-400' : 'text-red-400'}`}
                      />
                    </div>
                    <div className="ml-3">
                      <p
                        className={`text-sm font-medium ${
                          darkMode ? 'text-red-400' : 'text-red-800'
                        }`}
                      >
                        {error}
                      </p>
                    </div>
                    <div className="ml-auto pl-3">
                      <div className="-mx-1.5 -my-1.5">
                        <button
                          type="button"
                          onClick={() => setError('')}
                          className={`inline-flex rounded-md p-1.5 focus:ring-2 focus:ring-offset-2 focus:outline-hidden ${
                            darkMode
                              ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20 focus:ring-red-600 focus:ring-offset-gray-900'
                              : 'bg-red-50 text-red-500 hover:bg-red-100 focus:ring-red-600 focus:ring-offset-red-50'
                          }`}
                        >
                          <span className="sr-only">Dismiss</span>
                          <XMarkIcon aria-hidden="true" className="size-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <SecondaryButton
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  size="lg"
                  className="rounded-full"
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={loading} size="lg" className="rounded-full">
                  {loading ? 'Sending...' : 'Send Invitation'}
                </PrimaryButton>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
